import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Bell, Check, X, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import {
  fetchNotifications,
  notifAction,
  type Notification,
} from "@/api";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Empty } from "@/lib/dashboard";
import { Markdown } from "@/lib/markdown";

/**
 * NotificationBell — header bell icon with unread count badge.
 * Click → opens NotificationsPanel sheet (right edge).
 *
 * Polls /api/notifications every 30s; SSE-invalidated on broadcast.
 */
export default function NotificationBell() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const qc = useQueryClient();

  const notifsQ = useQuery({
    queryKey: ["notifications", unreadOnly],
    queryFn: () => fetchNotifications({ unread_only: unreadOnly }),
    refetchInterval: 30_000,
  });

  const items = notifsQ.data?.notifications ?? [];
  const unreadCount = notifsQ.data?.unread_count ?? 0;

  const ackMut = useMutation({
    mutationFn: (id: string) => notifAction(id, "ack"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const dismissMut = useMutation({
    mutationFn: (id: string) => notifAction(id, "dismiss"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setOpen(true);
          // Mark all visible notifications as 'seen' on open (optimistic, fire-and-forget)
          // Worker endpoint is per-id; we'll mark unseen ones individually.
          for (const n of items.filter((x) => !x.dashboard_seen_at)) {
            notifAction(n.id, "seen").catch(() => {});
          }
        }}
        title={t("notifications.title")}
        className="relative inline-flex items-center justify-center h-7 w-7 sm:h-8 sm:w-8 rounded-md border border-slate-700 bg-slate-900 text-slate-400 hover:text-slate-200 hover:border-slate-600"
      >
        <Bell className="h-3.5 w-3.5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-3.5 min-w-[14px] px-0.5 rounded-full bg-red-500 text-[9px] font-bold tabular-nums text-white flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-accent" />
              {t("notifications.title")}
              {unreadCount > 0 && (
                <Badge variant="red" className="!text-[10px]">
                  {t("notifications.unread", { n: unreadCount })}
                </Badge>
              )}
            </SheetTitle>
            <SheetDescription>
              <button
                onClick={() => setUnreadOnly((v) => !v)}
                className="text-[11px] text-accent hover:underline"
              >
                {unreadOnly ? t("notifications.show_all") : t("notifications.show_unread")}
              </button>
            </SheetDescription>
          </SheetHeader>

          <div className="p-4">
            {notifsQ.isLoading ? (
              <div className="flex items-center justify-center py-12 text-slate-400 text-sm gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            ) : items.length === 0 ? (
              <Empty>
                {unreadOnly
                  ? t("notifications.all_caught_up")
                  : t("notifications.no_notifications")}
              </Empty>
            ) : (
              <ul className="space-y-2">
                {items.map((n) => (
                  <NotifRow
                    key={n.id}
                    n={n}
                    onAck={() => ackMut.mutate(n.id)}
                    onDismiss={() => dismissMut.mutate(n.id)}
                  />
                ))}
              </ul>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

function NotifRow({
  n,
  onAck,
  onDismiss,
}: {
  n: Notification;
  onAck: () => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const isUnseen = !n.dashboard_seen_at;
  const isAcked = !!n.acknowledged_at;
  const isDismissed = !!n.dismissed_at;
  const hasFullBody = !!n.body_full_md && n.body_full_md.length > 0;
  const variant: "red" | "amber" | "default" =
    n.priority === "critical" ? "red" : n.priority === "high" ? "amber" : "default";
  return (
    <li
      className={`rounded-md border px-3 py-2 ${
        isUnseen
          ? "border-accent/40 bg-accent/5"
          : isDismissed
          ? "border-slate-800 bg-slate-900/30 opacity-60"
          : "border-slate-800 bg-slate-900/50"
      }`}
    >
      <div className="flex items-start gap-2">
        <Badge variant={variant} className="!text-[9px] !uppercase mt-0.5 flex-shrink-0">
          {n.priority}
        </Badge>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm text-slate-200">{n.title}</div>
          {n.body_short && (
            <p className="text-xs text-slate-400 mt-0.5">{n.body_short}</p>
          )}
          {hasFullBody && expanded && (
            <Markdown className="text-xs text-slate-300 mt-1.5 pl-2 border-l border-slate-700/60 space-y-0.5">
              {n.body_full_md as string}
            </Markdown>
          )}
          {hasFullBody && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-0.5 text-[10px] text-slate-500 hover:text-slate-300 mt-1"
            >
              {expanded ? (
                <>
                  <ChevronUp className="h-2.5 w-2.5" />
                  {t("notifications.collapse")}
                </>
              ) : (
                <>
                  <ChevronDown className="h-2.5 w-2.5" />
                  {t("notifications.expand")}
                </>
              )}
            </button>
          )}
          <div className="text-[10px] text-slate-500 mt-1 font-mono">
            {n.event_type} · {new Date(n.created_at).toLocaleString()}
          </div>
        </div>
      </div>
      {!isDismissed && (
        <div className="flex items-center justify-end gap-1.5 mt-1.5">
          {!isAcked && (
            <button
              type="button"
              onClick={onAck}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded border border-emerald-500/40 bg-emerald-950/30 text-emerald-300 hover:bg-emerald-950/50"
            >
              <Check className="h-2.5 w-2.5" />
              {t("notifications.ack")}
            </button>
          )}
          <button
            type="button"
            onClick={onDismiss}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded border border-slate-700 text-slate-400 hover:bg-slate-800"
          >
            <X className="h-2.5 w-2.5" />
            {t("notifications.dismiss")}
          </button>
        </div>
      )}
    </li>
  );
}
